/**
 * Ride and accommodation dates for an imported document.
 *
 * A document need not state any dates. The backend wants DD-MM-YYYY, and the
 * naive conversion of an empty string produced the literal "undefined-undefined-",
 * which the server rejected with:
 *   time data 'undefined-undefined-' does not match format '%d-%m-%Y'
 *
 * So the conversion refuses to invent a date, and the predicates below give the
 * screen a way to ask the traveller for what the document did not say, before
 * anything is submitted.
 */

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const BACKEND_DATE = /^\d{2}-\d{2}-\d{4}$/;

function isIsoDate(value) {
  return typeof value === 'string' && ISO_DATE.test(value.trim());
}

function isBackendDate(value) {
  if (typeof value !== 'string' || !BACKEND_DATE.test(value.trim())) return false;
  const [day, month] = value.trim().split('-').map(Number);
  return day >= 1 && day <= 31 && month >= 1 && month <= 12;
}

/** Convert YYYY-MM-DD to the DD-MM-YYYY the backend parses. '' if not a date. */
function toBackendDate(isoDate) {
  if (!isIsoDate(isoDate)) return '';
  const [year, month, day] = isoDate.trim().split('-');
  return `${day}-${month}-${year}`;
}

/**
 * Convert the DD-MM-YYYY the backend serves back to YYYY-MM-DD, the form every
 * helper in this file expects. '' if not a backend date -- including an
 * already-ISO string, which is left alone rather than mangled.
 */
function fromBackendDate(backendDate) {
  if (!isBackendDate(backendDate)) return '';
  const [day, month, year] = backendDate.trim().split('-');
  return `${year}-${month}-${day}`;
}

/**
 * Format a place's day and time the way the ride backend parses it:
 * '%m/%d/%Y, %I:%M:%S %p'. Returns '' when there is no usable date, so the
 * caller has to decide what that place's day should be rather than sending an
 * empty string the server cannot read.
 */
function toPlannedDate(isoDate, time) {
  if (!isIsoDate(isoDate)) return '';
  const [year, month, day] = isoDate.trim().split('-');

  const [rawHours, rawMinutes] = String(time || '').split(':');
  let hours = parseInt(rawHours, 10);
  let minutes = parseInt(rawMinutes, 10);
  // A place need not carry a time; and a time the document mangled must not turn
  // into "NaN:NaN", which the server cannot parse either.
  if (!Number.isFinite(hours) || hours < 0 || hours > 23) hours = 0;
  if (!Number.isFinite(minutes) || minutes < 0 || minutes > 59) minutes = 0;

  const meridiem = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  return `${parseInt(month, 10)}/${parseInt(day, 10)}/${year}, `
    + `${hour12}:${String(minutes).padStart(2, '0')}:00 ${meridiem}`;
}

/**
 * The ISO date `count` days after `isoDate`.
 *
 * Built through UTC so adding days cannot be shifted by the viewer's timezone or
 * by a daylight-saving change in the middle of a trip.
 */
function addDays(isoDate, count) {
  if (!isIsoDate(isoDate)) return '';
  const [year, month, day] = isoDate.trim().split('-').map(Number);
  const moved = new Date(Date.UTC(year, month - 1, day + count));
  return moved.toISOString().slice(0, 10);
}

/**
 * Which day a place belongs on.
 *
 * A booklet numbered "יום 1 / יום 2" gives no calendar date at all, so the day
 * number is the only thing placing an activity; resolved against the ride's
 * start, day 1 is the first day. A place that carries its own date keeps it.
 */
function resolvePointDate(point, rideStartDate) {
  const own = (point && point.date) || '';
  if (isIsoDate(own)) return own;

  const dayNumber = Number(point && point.dayNumber);
  if (Number.isInteger(dayNumber) && dayNumber >= 1 && isIsoDate(rideStartDate)) {
    return addDays(rideStartDate, dayNumber - 1);
  }
  return isIsoDate(rideStartDate) ? rideStartDate.trim() : '';
}

/** The end date a numbered itinerary implies, given where it starts. '' if unknown. */
function endDateForDayCount(startDate, dayCount) {
  const days = Number(dayCount);
  if (!isIsoDate(startDate) || !Number.isInteger(days) || days < 1) return '';
  return addDays(startDate, days - 1);
}

/** True when both ends of a range are real dates — a ride's, or a stay's. */
function hasBothDates(startDate, endDate) {
  return isIsoDate(startDate) && isIsoDate(endDate);
}

/**
 * Names of the accommodations that would be created without a usable stay.
 *
 * Only the ones that will actually be submitted are considered: still
 * processing, or discarded, means the traveller is not waiting on them.
 */
function accommodationsMissingDates(results) {
  return (results || [])
    .filter(result => result
      && result.status === 'done'
      && !result.discarded
      && result.type === 'accommodation')
    .filter((result) => {
      const data = (result.extracted && result.extracted.data) || {};
      return !hasBothDates(data.check_in_date, data.check_out_date);
    })
    .map((result) => {
      const data = (result.extracted && result.extracted.data) || {};
      return data.name || '';
    });
}

module.exports = {
  toBackendDate,
  fromBackendDate,
  toPlannedDate,
  hasBothDates,
  accommodationsMissingDates,
  addDays,
  resolvePointDate,
  endDateForDayCount,
};
